import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import CloudIcon from "@mui/icons-material/Cloud";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ComputerIcon from "@mui/icons-material/Computer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  AffectedResourceDto,
  RoadmapItemDto,
} from "../../../../../../customer-experience/src/lib/ShiOneClient";
import { getAwsCategoryDisplayLabel } from "../../utils/awsRecommendationsCategoryMappings";
import { AffectedScope } from "../../../../../../customer-experience/src/v2/src/features/recommendations-feature/utils/recommendationTypes";
import { getResourceItemKey } from "../../../../../../customer-experience/src/v2/src/features/recommendations-feature/utils/recommendationUtils";
import { transformSecurityDataToScopes } from "../../utils/awsRecommendationsUtils";
import { useState } from "react";

interface AwsRecommendationDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  recommendation: RoadmapItemDto | null;
}

const consolidateScopes = (scopes: AffectedScope[]): AffectedScope[] => {
  const scopeMap = new Map<string, AffectedResourceDto[]>();

  scopes.forEach((scope) => {
    if (scope.scopeId) {
      const existingResources = scopeMap.get(scope.scopeId) || [];
      if (scope.affectedResources) {
        scopeMap.set(scope.scopeId, [
          ...existingResources,
          ...scope.affectedResources,
        ]);
      } else if (!scopeMap.has(scope.scopeId)) {
        scopeMap.set(scope.scopeId, []);
      }
    }
  });

  return Array.from(scopeMap.entries()).map(([scopeId, resources]) => ({
    scopeId,
    affectedResources: resources,
  }));
};

export const AwsRecommendationDetailsDialog = ({
  open,
  onClose,
  recommendation,
}: AwsRecommendationDetailsDialogProps) => {
  const [expandedScopes, setExpandedScopes] = useState<
    Record<string, boolean>
  >({});
  const metadata = recommendation?.sourceMetadata || {};

  let parsedData: any = {};
  let affectedScopes: AffectedScope[] = [];
  let isSecurityRecommendation = false;

  if (metadata.AwsRecommendation) {
    try {
      parsedData = JSON.parse(metadata.AwsRecommendation);
    } catch (e) {
      console.error("Failed to parse AwsRecommendation data:", e);
    }
  } else if (metadata.securitySpecificMetadata) {
    try {
      parsedData = JSON.parse(metadata.securitySpecificMetadata);
      isSecurityRecommendation = true;
    } catch (e) {
      console.error("Failed to parse securitySpecificMetadata:", e);
    }
  }

  if (parsedData?.securitySpecificMetadata || parsedData?.controlId) {
    isSecurityRecommendation = true;
  }

  if (isSecurityRecommendation) {
    const securityMetadata = parsedData?.securitySpecificMetadata || parsedData;
    affectedScopes = transformSecurityDataToScopes(securityMetadata);
  } else {
    if (parsedData?.affectedScopes) {
      affectedScopes = parsedData.affectedScopes;
    } else if (metadata?.affectedScopes) {
      try {
        affectedScopes = JSON.parse(metadata.affectedScopes);
      } catch (e) {
        console.error("Failed to parse affectedScopes:", e);
      }
    }
  }

  const consolidatedScopes = consolidateScopes(affectedScopes);
  let useFormattedDescription = false;

  if (recommendation?.description) {
    useFormattedDescription = true;
  }

  const category = parsedData.category || metadata.category;
  const impact = parsedData.impact || metadata.impact;

  const totalAffectedScopes =
    parsedData.totalAffectedScopes ||
    metadata?.totalAffectedScopes ||
    consolidatedScopes.length;

  const totalAffectedResources =
    parsedData.totalAffectedResources ||
    metadata?.totalAffectedResources ||
    consolidatedScopes.reduce(
      (acc, sub) => acc + (sub.affectedResources?.length || 0),
      0,
    );

  const getImpactChipColor = (impactLevel: string | undefined) => {
    if (impactLevel === "High") return "error";
    if (impactLevel === "Medium") return "warning";
    if (impactLevel === "Low") return "success";
    return "default";
  };

  const toggleScope = (scopeId: string) => {
    setExpandedScopes((prev) => ({
      ...prev,
      [scopeId]: !prev[scopeId],
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CloudIcon sx={{ color: "text.secondary" }} />
          <Typography variant="h6">AWS Recommendation Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {category && (
              <Chip
                label={getAwsCategoryDisplayLabel(category)}
                size="small"
                variant="outlined"
              />
            )}
            {impact && (
              <Chip
                label={`Impact: ${impact}`}
                sx={{ borderColor: getImpactChipColor(impact) }}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {useFormattedDescription && recommendation?.description && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Description
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-line" }}
              >
                {recommendation.description}
              </Typography>
            </Box>
          )}
          <Divider />
          <Box sx={{ display: "flex", gap: 4 }}>
            {totalAffectedScopes !== undefined &&
              totalAffectedScopes !== null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Affected Scopes
                  </Typography>
                  <Typography variant="h6">{totalAffectedScopes}</Typography>
                </Box>
              )}
            {totalAffectedResources !== undefined &&
              totalAffectedResources !== null && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Affected Resources
                  </Typography>
                  <Typography variant="h6">{totalAffectedResources}</Typography>
                </Box>
              )}
          </Box>
          {consolidatedScopes.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Affected Resources
                </Typography>
                <List dense sx={{ maxHeight: 300, overflow: "auto" }}>
                  {consolidatedScopes.map((scope) => {
                    const uniqueKey = scope.scopeId || crypto.randomUUID();
                    return (
                      <Box key={uniqueKey}>
                        <ListItem
                          onClick={() => toggleScope(uniqueKey)}
                          sx={{ pl: 0 }}
                        >
                          <IconButton size="small">
                            {expandedScopes[uniqueKey] ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {isSecurityRecommendation
                                  ? scope.scopeId === "shared-resources"
                                    ? "Shared Resources"
                                    : scope.scopeId === "unknown"
                                      ? "Unknown Account"
                                      : `Account: ${scope.scopeId}`
                                  : scope.scopeId}{" "}
                              </Typography>
                            }
                            secondary={`${scope.affectedResources?.length || 0} resources`}
                          />
                        </ListItem>
                        <Collapse
                          in={expandedScopes[uniqueKey]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <List component="div" disablePadding>
                            {scope.affectedResources?.map(
                              (
                                resource: AffectedResourceDto,
                                resourcePosition,
                              ) => {
                                const resourceKey = getResourceItemKey(
                                  resource.resourceId,
                                  resourcePosition,
                                );

                                return (
                                  <ListItem key={resourceKey} sx={{ pl: 6 }}>
                                    <ComputerIcon
                                      sx={{
                                        mr: 1,
                                        fontSize: 18,
                                        color: "text.secondary",
                                      }}
                                    />
                                    <ListItemText
                                      primary={resource.resourceName}
                                      secondary={
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {resource.resourceType}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                );
                              },
                            )}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  })}
                </List>
              </Box>
            </>
          )}

          <Box
            sx={{
              backgroundColor: "action.hover",
              borderRadius: 1,
              p: 2,
              mt: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="body2">
              For more details, view your{" "}
              <Link
                href="/cloud/aws/recommendations"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                AWS Recommendations
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
